// FILE: src/pages/admin/AdminQuizResultsPage.jsx
// Quiz results by lesson (WP-7; MTO 11.1.3, matrix 5.3 / 9.8, B-14): every
// recorded attempt, for a date range, per lesson: attempts, students, the
// average of every attempt and of each student's latest one, the pass mark
// and how many attempts passed. GET /api/admin/dashboard/stats/quiz-stats
// ?from&to → { totalAttempts, students, passed, failed, noPassMark,
// averageScore, byResource[…], mostMissedQuestions: [], questionDataAvailable }.
// Per-question results need the new lesson player (WP-4); until then the
// page says so instead of showing an empty list.
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, LinearProgress, MenuItem, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import { getQuizStats } from "../../features/dashboardSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, StatCard } from "../../components/ui";
import { FigureGroup, ResponsiveTable } from "../../components/admin/AdminBits";
import { DASH, formatNumber } from "../../components/admin/format";
import { RANGE_PRESETS, presetRange } from "../../components/admin/dates";
import { resourceTypeLabel } from "../../i18n/labels";

const pct = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? DASH : `${formatNumber(v, 1)}%`);
/** The pass mark is a percent (assessment.passMark × 100): "80%", "72.5%". */
const passMarkText = (v) =>
  v === null || v === undefined || !Number.isFinite(Number(v))
    ? "None set"
    : `${formatNumber(v, Number.isInteger(Number(v)) ? 0 : 1)}%`;

export default function AdminQuizResultsPage() {
  const dispatch = useDispatch();
  const { quizStats, status = {}, errors = {} } = useSelector((state) => state.dashboard);
  const [range, setRange] = useState("all");

  const load = useCallback(() => dispatch(getQuizStats(presetRange(range))), [dispatch, range]);
  useEffect(() => {
    load();
  }, [load]);

  const q = quizStats || {};
  const rows = Array.isArray(q.byResource) ? q.byResource : [];
  const marked = (Number(q.passed) || 0) + (Number(q.failed) || 0);

  return (
    <Box>
      <PageHeader
        title="Quiz results"
        breadcrumbs={[{ label: "Dashboard", to: "/admin" }, { label: "Quiz results" }]}
        description="Scores recorded when students finish a lesson's quiz. Every attempt is kept; nothing is overwritten."
        actions={
          <TextField select id="quiz-page-range" label="Results for" value={range} onChange={(e) => setRange(e.target.value)} sx={{ minWidth: 220 }}>
            {RANGE_PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {!quizStats && status.quiz === "failed" ? (
        <ErrorState error={errors.quiz} title="We couldn't load the quiz results" onRetry={load} />
      ) : !quizStats ? (
        <LoadingState variant="page" label="Loading the quiz results…" />
      ) : (
        <>
          <Box sx={{ height: 8, mb: 1 }}>{status.quiz === "loading" && <LinearProgress aria-label="Updating the quiz results" />}</Box>
          {status.quiz === "failed" && <ErrorState compact error={errors.quiz} onRetry={load} />}
          <FigureGroup id="quiz-totals" title="All lessons" columns={4}>
            <StatCard
              icon={QuizOutlinedIcon}
              label="Attempts"
              value={formatNumber(q.totalAttempts ?? 0)}
              note={`By ${formatNumber(q.students ?? 0)} ${Number(q.students) === 1 ? "student" : "students"}`}
            />
            <StatCard
              icon={CheckCircleOutlineIcon}
              label="Passed"
              value={formatNumber(q.passed ?? 0)}
              note={marked ? `Of ${formatNumber(marked)} attempts with a pass mark` : "No attempts with a pass mark yet"}
            />
            <StatCard icon={HighlightOffOutlinedIcon} label="Below the pass mark" value={formatNumber(q.failed ?? 0)} />
            <StatCard
              icon={GradingOutlinedIcon}
              label="Average score"
              value={Number(q.totalAttempts) > 0 ? pct(q.averageScore) : DASH}
              note="Every attempt"
            />
          </FigureGroup>

          {q.questionDataAvailable === false && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Results for each question (such as the most-missed questions) will appear once the new lesson player records
              every answer. Until then, scores are per quiz attempt.
            </Alert>
          )}

          <Section id="by-lesson" title="By lesson" description="In course order." contentSx={{ p: { xs: 2, sm: 3 } }}>
            {rows.length === 0 ? (
              <EmptyState compact title="No quiz attempts yet" description="Results appear here when students finish a lesson's quiz." />
            ) : (
              <ResponsiveTable label="Quiz results by lesson" minWidth={900}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Lesson
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Attempts
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Students
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Average
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Average latest
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Pass mark
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Passed
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Below
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.resourceId}>
                      <TableCell component="th" scope="row">
                        <Typography component="span" sx={{ display: "block", fontWeight: 600 }}>
                          {r.title || "A lesson that no longer exists"}
                        </Typography>
                        {r.resourceType && (
                          <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                            {resourceTypeLabel(r.resourceType)}
                            {r.order != null ? ` ${r.order}` : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatNumber(r.attempts)}</TableCell>
                      <TableCell align="right">{formatNumber(r.students)}</TableCell>
                      <TableCell align="right">{pct(r.averageScore)}</TableCell>
                      <TableCell align="right">{pct(r.latestAverageScore)}</TableCell>
                      <TableCell align="right">{passMarkText(r.passMark)}</TableCell>
                      <TableCell align="right">{r.passed == null ? DASH : formatNumber(r.passed)}</TableCell>
                      <TableCell align="right">{r.failed == null ? DASH : formatNumber(r.failed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveTable>
            )}
            <Typography sx={{ color: "text.secondary", mt: 1.5 }}>
              "Average latest" counts only each student's most recent attempt.
            </Typography>
          </Section>
        </>
      )}
    </Box>
  );
}
